// File: frontend/src/components/admin/tabs/OverviewTab.js
//
// The headline view. GET /api/admin/stats already returned more than the old
// dashboard drew — totalHabits, totalCompletions, avgHabitsPerUser, the whole
// thisWeek and thisMonth blocks, two of the four trend series, and the entire
// engagement block including the power-user list — all fetched every load and
// thrown away. Those are surfaced here.
import { Users, Activity, TrendingUp, Calendar, UserPlus, Target, CheckCircle, PieChart, Zap, Flame } from 'lucide-react';
import { Card, StatCard, StatGrid, EmptyState } from '../primitives';
import { BarChart, DonutChart, RankedBars, CHART_TOKENS } from '../charts';

// Today vs yesterday, as one comparison rather than two loose numbers.
function DeltaCard({ label, today, yesterday, icon: Icon, tone }) {
  const delta = (today ?? 0) - (yesterday ?? 0);
  const direction = delta === 0 ? null : delta > 0 ? 'up' : 'down';
  return (
    <StatCard
      title={label}
      value={today}
      subtitle={`${yesterday ?? 0} yesterday`}
      icon={Icon}
      tone={tone}
      trend={direction}
      trendValue={direction ? `${delta > 0 ? '+' : ''}${delta}` : null}
    />
  );
}

export default function OverviewTab({ stats, onOpenUser }) {
  if (!stats) return null;
  const { overview, today, yesterday, thisWeek, thisMonth, trends, topHabits, authTypes, engagement } = stats;

  const authSlices = [
    { label: 'Email only', value: authTypes?.emailOnly || 0 },
    { label: 'Google only', value: authTypes?.googleOnly || 0 },
    { label: 'Apple only', value: authTypes?.appleOnly || 0 },
    // Falls back to the deprecated `both` key so a dashboard talking to an
    // older server still draws this slice instead of dropping it.
    { label: 'Multiple', value: authTypes?.multiple ?? authTypes?.both ?? 0 },
  ];

  return (
    <div className="space-y-6 sm:space-y-8">
      <section>
        <h2 className="text-base sm:text-lg font-semibold text-ink-main mb-3 flex items-center gap-2">
          <Zap className="text-warning" size={20} aria-hidden="true" />Right now
        </h2>
        <StatGrid cols={5}>
          <StatCard
            title="Total users" value={overview.totalUsers} icon={Users} tone="info"
            trend={overview.growthRate > 0 ? 'up' : overview.growthRate < 0 ? 'down' : null}
            trendValue={`${overview.growthRate}%`}
            subtitle="Month over month"
          />
          <StatCard title="Active today" value={overview.dailyActiveUsers} subtitle="Logged a habit" icon={Activity} tone="success" />
          <StatCard title="Active this week" value={overview.weeklyActiveUsers} subtitle="Last 7 days" icon={Calendar} tone="success" />
          <StatCard title="Active this month" value={overview.monthlyActiveUsers} subtitle="Last 30 days" icon={TrendingUp} tone="success" />
          <StatCard
            title="Weekly reach"
            value={overview.retentionRate != null ? `${overview.retentionRate}%` : '—'}
            subtitle="Weekly active ÷ all users"
            icon={Target}
            tone="accent"
            // This used to be labelled "Retention Rate", which it is not: it
            // falls as the user base grows even when every cohort behaves
            // identically. Real retention is on the Insights tab.
            hint="Not retention — see Insights for signup cohorts."
          />
        </StatGrid>
      </section>

      <section>
        <h2 className="text-base sm:text-lg font-semibold text-ink-main mb-3 flex items-center gap-2">
          <Activity className="text-info" size={20} aria-hidden="true" />Today vs yesterday
        </h2>
        <StatGrid cols={3}>
          <DeltaCard label="New users" today={today.newUsers} yesterday={yesterday.newUsers} icon={UserPlus} tone="success" />
          <DeltaCard label="Active users" today={today.activeUsers} yesterday={yesterday.activeUsers} icon={Activity} tone="info" />
          <DeltaCard label="Completions" today={today.completions} yesterday={yesterday.completions} icon={CheckCircle} tone="accent" />
        </StatGrid>
      </section>

      {/* thisWeek / thisMonth and the library totals were fetched on every load
          and never drawn. */}
      <section>
        <h2 className="text-base sm:text-lg font-semibold text-ink-main mb-3 flex items-center gap-2">
          <Target className="text-success" size={20} aria-hidden="true" />The library
        </h2>
        <StatGrid cols={4}>
          <StatCard title="Habits tracked" value={overview.totalHabits} subtitle={`${overview.avgHabitsPerUser} per user with habits`} icon={Target} tone="accent" />
          <StatCard title="Completions all time" value={overview.totalCompletions} icon={CheckCircle} tone="success" />
          <StatCard title="New this week" value={thisWeek?.newUsers} subtitle={`${thisWeek?.activeUsers ?? 0} active`} icon={UserPlus} tone="info" />
          <StatCard title="New this month" value={thisMonth?.newUsers} subtitle={`${thisMonth?.activeUsers ?? 0} active`} icon={TrendingUp} tone="info" />
        </StatGrid>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <Card title="Signups (30 days)" icon={UserPlus} tone="success">
          <BarChart data={trends.signups} token={CHART_TOKENS[2]} height={140} label="Daily signups over the last 30 days" />
        </Card>
        <Card title="Active users (30 days)" icon={Activity} tone="info">
          <BarChart data={trends.activity} token={CHART_TOKENS[1]} height={140} label="Daily active users over the last 30 days" />
        </Card>
        {/* Both of these were returned by the API and never rendered. */}
        <Card title="Completions (30 days)" icon={CheckCircle} tone="accent">
          <BarChart data={trends.completions} token={CHART_TOKENS[0]} height={140} label="Daily completions over the last 30 days" />
        </Card>
        <Card title="Habits created (30 days)" icon={Target} tone="warning">
          <BarChart data={trends.habits} token={CHART_TOKENS[3]} height={140} label="Habits created per day over the last 30 days" />
        </Card>
        <Card title="How people sign in" icon={PieChart} tone="info">
          <DonutChart slices={authSlices} size={120} />
          {authTypes?.none > 0 && (
            <p className="text-xs text-danger mt-3">
              {authTypes.none} account{authTypes.none === 1 ? '' : 's'} with no credential at all — worth investigating.
            </p>
          )}
        </Card>
        <Card title="Most common habits" icon={Target} tone="accent">
          <RankedBars
            rows={(topHabits || []).slice(0, 6).map(h => ({ label: h.name, value: Number(h.count) }))}
            token={CHART_TOKENS[0]}
          />
        </Card>
      </section>

      {/* The engagement block was fetched every load and never drawn at all. */}
      <section>
        <h2 className="text-base sm:text-lg font-semibold text-ink-main mb-3 flex items-center gap-2">
          <Flame className="text-warning" size={20} aria-hidden="true" />Engagement
        </h2>
        {/* items-start so the single stat does not stretch to the height of
            the power-user list beside it. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 items-start">
          <StatCard
            title="Completions per active user"
            value={engagement?.avgCompletionsPerActiveUser ?? '—'}
            subtitle="Last 7 days"
            icon={Activity}
            tone="success"
          />
          <Card title="Most active people (30 days)" icon={Users} tone="accent">
            {engagement?.powerUsers?.length ? (
              <ul className="space-y-1">
                {engagement.powerUsers.map((u) => (
                  <li key={u.id ?? u.email}>
                    <button
                      type="button"
                      onClick={() => u.id != null && onOpenUser?.(u.id)}
                      disabled={u.id == null}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm bg-surface-input hover:bg-[var(--color-hover-surface)] disabled:pointer-events-none text-left"
                    >
                      <span className="text-ink-main truncate">{u.email}</span>
                      <span className="admin-nums text-ink-light shrink-0">{u.completions}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : <EmptyState>No completions in the last 30 days</EmptyState>}
          </Card>
        </div>
      </section>
    </div>
  );
}
